import React, { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Paper,
  List,
  ListItem,
  ListItemText,
  Box,
  Stack,
  Button,
} from "@mui/material";
import Logo from "../../logo/Logo.svg";

const PrivacyPolicy = () => {
  useEffect(() => {
    const sections = document.querySelectorAll(".section-content");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.2 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const privacySections = [
    {
      title: "1. Information We Collect",
content: (
  <List>
    <ListItem>
      <ListItemText primary="We may collect the following types of information:" />
    </ListItem>
    <ListItem>
    <ListItemText primary={<b>1.1 Information You Provide to Us:</b>}/>
    </ListItem>
    <List>
      <ListItem>
        <ListItemText primary="Account Information: When you create an account, we may collect your name, email address, username, and password." />
      </ListItem>
      <ListItem>
        <ListItemText primary="Feedback and Support: If you contact us for support or provide feedback, we may collect your contact information and any details you share with us." />
      </ListItem>
    </List>
    <ListItem>
      <ListItemText primary={<b>1.2 Information Collected Automatically:</b>}/>
    </ListItem>
    <List>
      <ListItem>
        <ListItemText primary="Usage Data: Information about your interactions with the Service, such as pages viewed, features used, and time spent on the platform." />
      </ListItem>
      <ListItem>
        <ListItemText primary="Device Information: Details about the device you use to access the Service, including your IP address, browser type, operating system, and device identifiers." />
      </ListItem>
    </List>
    <ListItem>
      <ListItemText primary={<b>1.3 Cookies and Tracking Technologies:</b>}/>
    </ListItem>
    <List>
      <ListItem>
        <ListItemText primary="We use cookies, web beacons, and similar technologies to collect information about your use of the Service. You can manage your cookie preferences in your browser settings." />
      </ListItem>
    </List>
  </List>
),
    },
    {
      title: "2. How We Use Your Information",
            content: (
              <List>
                <ListItem>
                  <ListItemText primary="We use the information we collect for the following purposes:" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="To provide, operate, and improve the Service & User Experience." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="To communicate with you, including responding to inquiries and sending updates about the Service." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="To analyze usage trends and user behavior to enhance user experience." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="To comply with legal obligations and enforce our Terms of Use." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="To secure and show the right features on right moment in apps" />
                </ListItem>
              </List>
            ),
    },
    {
      title: "3. Sharing of Information",
            content: (
              <List>
                <ListItem>
                  <ListItemText primary="We do not sell your personal information to third parties. However, we may share your information in the following circumstances:" />
                </ListItem>
                <ListItem>
                  <ListItemText primary={<><b>With Service Providers:</b> Third-party vendors who assist in operating and maintaining the Service, subject to confidentiality agreements.</>} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={<><b>For Legal Compliance:</b> When required by law or to protect our rights, privacy, safety, or property.</>} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={<><b>With Your Consent:</b> When you explicitly agree to share your information.</>} />

                </ListItem>
              </List>
            ),
    },
    {
      title: "4. Data Security",
      content:
        "We implement reasonable technical and organizational measures to protect your information from unauthorized access, use, or disclosure. However, no data transmission or storage system can be guaranteed to be 100% secure.",
    },
    {
      title: "5. Your Choices and Rights",
            content: (
              <List>
                <ListItem>
                  <ListItemText primary="You have the following rights regarding your information:" />
                </ListItem>
                <ListItem>
                  <ListItemText primary={<><b>Access and Update:</b> You can access and update your account information through your profile settings.</>} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={<><b>Delete Your Data:</b> You may request deletion of your account and personal data by contacting us at support@betaappstore.com.</>} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={<><b>Opt-Out:</b> You can opt out of receiving promotional communications by following the unsubscribe instructions in our emails.</>} />
                </ListItem>
              </List>
            ),
    },
    {
      title: "6. Retention of Data",
      content:
        "We retain your information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy or as required by law.",
    },
    {
      title: "7. Third-Party Links",
      content:
        "The Service may contain links to third-party websites or services. We are not responsible for the privacy practices or content of these third parties. Please review their privacy policies before providing any personal information.",
    },
    {
      title: "8. Changes to This Privacy Policy",
      content:
        "We may update this Privacy Policy from time to time. Any changes will be effective upon posting the revised policy. Your continued use of the Service constitutes your acceptance of the updated Privacy Policy.",
    },
    {
      title: "12. Contact Us",
      content: (
        <>
          If you have any questions or concerns about this Privacy Policy, please contact us at <a href="mailto:support@betaappstore.com" className="text-blue-500 underline">support@betaappstore.com</a>
          <br />
          Thank you for trusting Beta Appstore. Your privacy is important to us!
        </>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", background: "var(--ba-bg-cream)" }}>
      <AppBar position="sticky" sx={{ background: "var(--ba-bg)", borderBottom: "1px solid var(--ba-line)" }}>
        <Toolbar sx={{ gap: 2 }}>
          <Box component={RouterLink} to="/landing" sx={{ display: "flex", alignItems: "center" }}>
            <Box component="img" src={Logo} alt="Beta Appstore" sx={{ height: 36 }} />
          </Box>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1, color: "text.primary", ml: 2 }}>
            Privacy Policy
          </Typography>
          <Button component={RouterLink} to="/register" variant="contained" size="small">
            Get started
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, border: "1px solid", borderColor: "divider", mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            <strong>Effective Date:</strong> 05 Jan, 2025
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Barquecon Technologies Pvt Ltd ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use Beta Appstore ("the Service"). Please read this policy carefully — by using the Service you agree to the collection and use of your information as described here.
          </Typography>
        </Paper>

        <Stack spacing={2}>
          {privacySections.map((section, index) => (
            <Paper
              key={index}
              elevation={0}
              className="section-content"
              sx={{
                p: { xs: 3, md: 4 },
                borderLeft: "4px solid",
                borderLeftColor: "primary.main",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="h4" component="h2" sx={{ mb: 1.5, color: "text.primary" }}>
                {section.title}
              </Typography>
              <Typography variant="body1" component="div" sx={{ color: "text.secondary" }}>
                {section.content}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Container>

      <Box component="footer" sx={{ borderTop: "1px solid var(--ba-line)", py: 3, background: "var(--ba-bg)" }}>
        <Container maxWidth="md">
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box component="img" src={Logo} alt="" sx={{ height: 28 }} />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                © 2025 Barquecon Technologies Pvt Ltd.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography component={RouterLink} to="/terms-of-use" variant="body2" sx={{ color: "primary.dark", textDecoration: "none", fontWeight: 600 }}>
                Terms of use
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
